import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export interface CostCenterDto {
  costCenterId: string;
  code: string;
  nameAr: string;
  nameEn: string | null;
  parentCostCenterId: string | null;
  isGroup: boolean;
  isActive: boolean;
}

export interface CostCenterInput {
  code: string;
  nameAr: string;
  nameEn?: string | null;
  parentCostCenterId?: string | null;
  isGroup: boolean;
  isActive?: boolean;
}

const QK = ['cost-centers'] as const;

export function useCostCenters() {
  return useQuery({
    queryKey: QK,
    queryFn: async () => (await apiClient.get<CostCenterDto[]>('/api/accounting/cost-centers')).data,
  });
}

export function useCreateCostCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CostCenterInput) =>
      (await apiClient.post<CostCenterDto>('/api/accounting/cost-centers', req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useUpdateCostCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, req }: { id: string; req: CostCenterInput }) =>
      (await apiClient.put<CostCenterDto>(`/api/accounting/cost-centers/${id}`, req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useDeleteCostCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/accounting/cost-centers/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
